import { useFormatDate } from 'components/misc/FormattedDate';
import { ClassifierData } from 'types/classifiers';
import { IdentityAddress, IdentityFields } from 'types/clientIdentification';
import { PartyType } from 'types/contacts';
import { getClassifierText } from 'utils/getTranslations';

export interface IdentityValueContext {
    countryName: (code: string | null) => string | null;
    formatDate: (value: string | null) => string | null;
}

export function useIdentityValueContext(classifiers: Record<string, ClassifierData>): IdentityValueContext {
    const formatDate = useFormatDate();
    return {
        countryName: (code) => (code ? getClassifierText(classifiers['COUNTRY'], code) || code : null),
        formatDate: (value) => formatDate(value),
    };
}

const joinPresent = (parts: (string | null | undefined)[], separator: string): string | null => {
    const present = parts.filter((part): part is string => !!part);
    return present.length ? present.join(separator) : null;
};

export function addressText(address: IdentityAddress, countryName: IdentityValueContext['countryName']) {
    return joinPresent(
        [
            address.address1,
            address.address2,
            joinPresent([address.zip, address.city], ' '),
            countryName(address.countryCode),
        ],
        ', '
    );
}

export function personNames(fields: IdentityFields): string | null {
    return joinPresent([fields.firstName, fields.lastName], ' ') ?? fields.name;
}

export function companyNameAndForm(fields: IdentityFields): string | null {
    return joinPresent([fields.officialName ?? fields.name, fields.legalForm], ' · ');
}

export function itemValue(
    key: string,
    fields: IdentityFields,
    partyType: PartyType,
    { countryName, formatDate }: IdentityValueContext
): string | null {
    const isCompany = partyType === PartyType.COMPANY;
    switch (key) {
        case 'NAME_AND_LEGAL_FORM':
            return companyNameAndForm(fields);
        case 'NAMES':
            return isCompany ? companyNameAndForm(fields) : personNames(fields);
        case 'REGISTRATION_CODE':
            return fields.regCode;
        case 'PERSONAL_CODE':
            return isCompany ? fields.regCode : fields.personalCode;
        case 'REGISTRATION_ADDRESS':
        case 'HOME_ADDRESS':
            return addressText(fields.identificationAddress, countryName);
        case 'PRINCIPAL_PLACE_OF_BUSINESS':
            return addressText(fields.visitingAddress, countryName);
        case 'REGISTRATION_DATE':
            return formatDate(fields.registrationDate);
        case 'REGISTRATION_AUTHORITY':
            return fields.registrationAuthority;
        case 'NATURE_OF_BUSINESS':
            return fields.natureOfBusiness;
        case 'DATE_OF_BIRTH':
            return formatDate(fields.dateOfBirth);
        case 'PLACE_OF_BIRTH':
            return joinPresent([fields.placeOfBirthCity, countryName(fields.placeOfBirthCountryCode)], ', ');
        case 'NATIONALITIES':
            return joinPresent(fields.nationalities.map(countryName), ', ');
        case 'ID_DOCUMENT_NUMBER':
            return joinPresent(
                [
                    fields.identityDocument.number,
                    countryName(fields.identityDocument.issuingCountryCode),
                    formatDate(fields.identityDocument.expiryDate),
                ],
                ' · '
            );
        case 'OCCUPATION':
            return fields.occupation;
        default:
            return null;
    }
}
